import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { defer } from 'rxjs/internal/observable/defer';
import { of } from 'rxjs/internal/observable/of';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { Job } from './job.model';

interface DestinatorWithType {
    destinator: string;
    destinatorType: string;
}

@Injectable({
    providedIn: 'root' // No need to provide in AppModule, and now tree shakable
})
export class JobService {

    defaultSendRulesLeft: Array<any> = [
        {rule : 'stopOnSuccess', mediaDestinatorType : 'email'},
        {rule : 'stopOnSuccess', mediaDestinatorType : ''},
        {rule : 'stopOnSuccess', mediaDestinatorType : ''}
        ];

    constructor(private httpClient: HttpClient) {
    }

    sendAR3Test(context, userId, userName, contentType, templateFileRef,
                templateId, destinator, mediaType, deviceId): Observable<any> {
        return this.httpClient
            .post('servlets/jobs/ar3test/', { context, userId, userName, contentType,
                                              templateFileRef, templateId, destinator, mediaType, deviceId,
                                              headers: {'Content-Type': 'application/json; charset=utf-8'}})
            .pipe(
                map((response: any) => response)
            );
    }

    wasDestinatorUsedAlready(destinatorsWithTypes: Array<DestinatorWithType>, destinatorIndex: number): boolean {
        for (let i = 0; i < destinatorIndex; ++i) {
            if (destinatorsWithTypes[i].destinator === destinatorsWithTypes[destinatorIndex].destinator) {
                return true;
            }
        }

        return false;
    }

    sendLatepaymentMessage(destinatorsWithTypes: Array<DestinatorWithType>, destinatorIndex: number,
                           context, jobId, userName, templateId, destinatorPrefix,
                           rowIndex, detailCellCallback: Function): Observable<any> {

        if (destinatorIndex === destinatorsWithTypes.length) {
            // All items have been handled, return an observable that completes
            return of(null);
        }

        const destinatorWithType = destinatorsWithTypes[destinatorIndex];
        if (this.wasDestinatorUsedAlready(destinatorsWithTypes, destinatorIndex)) {
            detailCellCallback(rowIndex, destinatorWithType.destinatorType, destinatorWithType.destinator, 'skip');

            return of(null);
        }

        const contentType = 'relation:latepayment';

        const mediaType: string = this.getMediaType(destinatorWithType.destinatorType);

        const postRequest = defer(() => {
            console.log('concatMap[' + rowIndex + ']: ' + destinatorWithType.destinatorType + ' : ' + destinatorWithType.destinator);

            return this.httpClient
                .post('servlets/jobs/latepaymentmessage/', {
                    userName,
                    jobId,
                    templateId,
                    contentType,
                    mediaType,
                    destinatorType: destinatorWithType.destinatorType,
                    destinator: destinatorWithType.destinator,
                    destinatorPrefix, // Currently not used by Central's API
                    context,
                    headers: {'Content-Type': 'application/json; charset=utf-8'}
                }) // Response from API is not important
                .pipe(
                    tap(response => {
                        detailCellCallback(rowIndex, destinatorWithType.destinatorType, destinatorWithType.destinator, 'OK');
                    }),
                    catchError(error => {
                        detailCellCallback(rowIndex, destinatorWithType.destinatorType, destinatorWithType.destinator, 'FAIL');

                        // Continue with the next item in the sequence
                        return of(null);
                    })
                );
        });

        // Recursively handle the next item in the array
        return postRequest.pipe(
            concatMap(() => this.sendLatepaymentMessage(destinatorsWithTypes, destinatorIndex + 1,
                context, jobId, userName, templateId, destinatorPrefix,
                rowIndex, detailCellCallback)));
    }


    getMediaType(destinatorType: String): string {
        if (destinatorType === 'email') {

            return 'email';
        }

        const parts = destinatorType.split('-');

        return parts[0];
    }


    sanitizeEmailSubject(content): any {
        let sanitizeContent = content.replace(/\&nbsp[;]?/g, ' ');
        // Match any tag within <> with negative lookahead skipping those with 'comment' in tag name and remove tags
        sanitizeContent = sanitizeContent.replace(/(<((?!comment|\/comment))([^>]+)>)/gi, '');

        return sanitizeContent;
    }

    sanitizeSMS(data: any): any {
        let smsMessage: any = {...data.jobContent.template.smsMessage};
        if (smsMessage !== undefined) {
            // Replace <br> and <br/> and <br /> with \n
            smsMessage = smsMessage.replace(/<br\s*\/?>/g, '\n');
            // Match any tag within <> with negative lookahead skipping those with 'comment' in tag name
            smsMessage = smsMessage.replace(/(<((?!comment|\/comment))([^>]+)>)/gi, '');

            // Replace characters not in SMS ASCII
            smsMessage = smsMessage.replace('´', '\'');
            smsMessage = smsMessage.replace('“', '\"');

            // TODO: Remove unwanted html already when editing the text
           //  data.jobContent.template.smsMessage = smsMessage;
        }

        return smsMessage;
    }

    createDefaultJob(template, mediaTypes): Job {
        const newJob: Job = {
            jobCategory: template.communicationsCategoryId,
            jobName: '',
            jobDescription: '',
            jobState: 'draft',
            jobContent: {
                template: template.templateDefaultContent,
                mediaTypeMode: true,
                sendRules: this.defaultSendRules(template),
                media: mediaTypes.media
            }
        };

        return newJob;
    }

    defaultSendRules(template): any {
        let sendRules;
        if (template.communicationsCategoryId === 'recalls' || template.communicationsCategoryId === 'missed') {
            sendRules = [
                {rule : 'escalate', mediaDestinatorType : 'email'},
                {rule : 'escalate', mediaDestinatorType : '', daysAfterFirst: 0},
                {rule : 'escalate', mediaDestinatorType : '', daysAfterFirst: 0},
                {rule : 'escalate', mediaDestinatorType : 'stop', daysAfterFirst: 5}
            ];
        } else {
            sendRules = {...this.defaultSendRulesLeft};
        }

        return sendRules;
    }
}
